/* eslint-disable no-param-reassign */
import React, { useRef, useEffect } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import 'video.js/dist/video-js.css';
import styled from '@emotion/styled';
import { PLAYER_HOTKEYS } from '@constants/unacademyIcons/icons.constants';
import { useLessonPlayer } from '@stores/lessonPlayerStore';

interface IconsVideoPlayerProps {
  options: any;
  onReady: any;
  currentLessonToken?: string;
}

const StyledVideo = styled.video`
  width: 100%;
  z-index: 1;
`;

const IconsVideoPlayer: React.FC<IconsVideoPlayerProps> = ({
  options,
  onReady,
  currentLessonToken
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<null | VideoJsPlayer>(null);
  const lessonToken = useLessonPlayer.use.lessonToken();

  useEffect(() => {
    const isSourceValid = options.sources?.[0]?.src;
    // make sure Video.js player is only initialized once
    if (!playerRef.current && isSourceValid) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      // eslint-disable-next-line no-multi-assign
      const player = (playerRef.current = videojs(
        videoElement,
        {
          ...options,
          preferFullscreen: true,
          userActions: {
            ...options.userActions,
            hotkeys: {
              playPauseKey: () => false,
              muteKey: (event) => event.which === PLAYER_HOTKEYS.M,
              fullscreenKey: (event) => event.which === PLAYER_HOTKEYS.F
            }
          }
        },
        () => {
          onReady(player);
        }
      ));

      const playerElem = document.getElementById('icons-video-player');
      const controlsElem = document.getElementById('icons-controls-wrapper');

      if (playerElem && controlsElem) {
        // To support custom controls inside the video wrapper in fullscreen mode
        playerElem.append(controlsElem);
      }
    }
  }, [options, videoRef, onReady, lessonToken, currentLessonToken]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player !== null) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  useEffect(() => {
    if (currentLessonToken || lessonToken) {
      (videojs as any).Hls.xhr.beforeRequest = (options: any) => {
        if (options.headers === undefined) {
          options.headers = {};
        }
        options.headers['x-access-resource'] =
          currentLessonToken || lessonToken;

        return options;
      };
    }
    return () => {
      (videojs as any).Hls.xhr.beforeRequest = (options: any) => {
        options.headers = {};
        return options;
      };
    };
  }, [lessonToken, currentLessonToken]);

  return (
    <div data-vjs-player className="icons-player-wrapper">
      <StyledVideo
        ref={videoRef}
        id="icons-video-player"
        preload="none"
        className="video-js vjs-big-play-centered"
      >
        <track kind="captions" />
      </StyledVideo>
    </div>
  );
};

IconsVideoPlayer.displayName = 'Icons Video Player';

export default IconsVideoPlayer;
